import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api, bigArt, mediumArt, formatTime } from "@/lib/api";
import { usePlayer } from "@/contexts/PlayerContext";
import { Play, Pause, ChevronRight, Radar, Sparkles, Calendar, Plus } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import ArtistAvatar from "@/components/ArtistAvatar";
import SmartImage from "@/components/SmartImage";

/* ----------------------------- Skeleton ----------------------------- */
const HeroSkeleton = () => (
  <div className="h-[340px] sm:h-[420px] rounded-3xl bg-white/[0.04] animate-pulse mb-12" />
);

const RowSkeleton = ({ count = 5 }) => (
  <div className="flex gap-5 mb-12">
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="w-44 sm:w-56 aspect-square rounded-2xl bg-white/[0.04] animate-pulse flex-shrink-0" />
    ))}
  </div>
);

/* --------------------------- Hero Feature --------------------------- */
const HeroFeature = ({ hero, songs, onPlay }) => {
  if (!hero) return null;
  const art = bigArt(hero.artworkUrl100);
  return (
    <Link
      to={hero.link || "#"}
      data-testid="hero-feature"
      data-testid-kind={hero.kind}
      className="group relative block rounded-3xl overflow-hidden mb-12 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.6)]"
    >
      {/* Background tinted glow from artwork */}
      <div className="absolute inset-0 -z-10">
        <img
          src={art}
          alt=""
          className="w-full h-full object-cover blur-3xl scale-150 opacity-60"
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/40 to-transparent" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-[1.4fr_1fr] items-center gap-6 sm:gap-10 p-7 sm:p-10 min-h-[340px] sm:min-h-[420px]">
        {/* Text side */}
        <div>
          <p className="text-[11px] uppercase tracking-[0.35em] text-white/70 font-semibold mb-3 sm:mb-4">
            {hero.label}
          </p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tighter leading-[0.95] mb-3 text-shadow-lg">
            {hero.collectionName}
          </h2>
          <p className="text-base sm:text-lg text-white/80 mb-2">{hero.artistName}</p>
          {hero.primaryGenreName && (
            <p className="text-sm text-white/50">{hero.primaryGenreName}</p>
          )}
          <div className="flex gap-3 mt-7">
            <button
              data-testid="hero-play-btn"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onPlay && onPlay();
              }}
              className="bg-white text-black font-semibold rounded-full px-6 py-3 flex items-center gap-2 hover:scale-[1.04] transition-transform shadow-lg"
            >
              <Play size={18} fill="currentColor" /> {hero.cta_play_label || "Play"}
            </button>
            <span className="bg-white/10 hover:bg-white/20 backdrop-blur-md border border-white/15 text-white font-medium rounded-full px-6 py-3 transition flex items-center gap-1">
              {hero.kind === "upcoming" ? "View release" : hero.kind === "upload" ? "View artist" : "View album"} <ChevronRight size={16} />
            </span>
          </div>
        </div>

        {/* Artwork side */}
        <div className="hidden sm:flex justify-center sm:justify-end">
          <div className="relative w-[280px] h-[280px] lg:w-[340px] lg:h-[340px] rounded-2xl overflow-hidden shadow-[0_20px_50px_-12px_rgba(0,0,0,0.7)] group-hover:scale-[1.02] transition-transform duration-500">
            <img src={art} alt={hero.collectionName} className="w-full h-full object-cover" />
          </div>
        </div>
      </div>
    </Link>
  );
};

/* --------------------------- Apple-Music-style NEW pill ---------------------------
 * Tiny helper so every bespoke Home row card can flag fresh drops with
 * the same vermilion pill the shared AlbumCard uses. We carry the
 * computation inline (rather than imported) because each row card has
 * its own positioning needs — the badge container is the artwork div.
 *
 * NEW window: 60 days from releaseDate (generous; matches AlbumCard).
 */
const isAlbumNew = (releaseDate) => {
  if (!releaseDate) return false;
  try {
    const released = new Date(releaseDate);
    if (Number.isNaN(released.getTime())) return false;
    const ageMs = Date.now() - released.getTime();
    return ageMs >= 0 && ageMs <= 60 * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
};

const NewPill = ({ id, position = "top-2 right-2" }) => (
  <span
    data-testid={`album-new-badge-${id}`}
    className={`absolute ${position} text-[9px] font-black uppercase tracking-[0.18em] px-1.5 py-0.5 rounded-sm bg-vermilion text-white shadow-[0_4px_14px_-2px_rgba(232,69,37,0.6)] z-10`}
  >
    New
  </span>
);

/* --------------------------- Album Tile (Apple-like) --------------------------- */
const AppleAlbumTile = ({ album, large = false }) => (
  <Link
    to={`/album/${album.collectionId}`}
    data-testid={`apple-album-${album.collectionId}`}
    className="group flex flex-col flex-shrink-0"
    style={{ width: large ? "240px" : "190px" }}
  >
    <div className="aspect-square rounded-2xl overflow-hidden mb-3 shadow-lg relative">
      <img
        src={mediumArt(album.artworkUrl100)}
        alt={album.collectionName}
        loading="lazy"
        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
      />
      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
      {isAlbumNew(album.releaseDate) && <NewPill id={album.collectionId} />}
    </div>
    <p className="text-[15px] font-medium leading-tight truncate">{album.collectionName}</p>
    <p className="text-[13px] text-white/55 truncate mt-0.5">{album.artistName}</p>
  </Link>
);

/* --------------------------- Numbered Top Songs --------------------------- */
const TopSongTile = ({ song, rank, onPlay, isCurrent, isPlaying }) => (
  <div
    data-testid={`top-song-${song.trackId}`}
    className="group flex items-center gap-4 p-2 pr-4 rounded-2xl bg-white/[0.03] hover:bg-white/[0.07] transition cursor-pointer border border-white/5"
    onClick={onPlay}
  >
    <div className="relative flex-shrink-0">
      <img
        src={mediumArt(song.artworkUrl100)}
        alt=""
        className="w-14 h-14 rounded-xl object-cover"
      />
      <div className="absolute inset-0 rounded-xl bg-black/0 group-hover:bg-black/40 flex items-center justify-center transition">
        {isPlaying && isCurrent ? (
          <Pause size={20} className="opacity-0 group-hover:opacity-100 text-white" fill="currentColor" />
        ) : (
          <Play size={20} className="opacity-0 group-hover:opacity-100 text-white" fill="currentColor" />
        )}
      </div>
    </div>
    <span className="text-2xl sm:text-3xl font-display font-semibold text-white/30 tabular-nums w-8 text-right">
      {rank}
    </span>
    <div className="flex-1 min-w-0">
      <p className={`text-[15px] font-medium truncate ${isCurrent ? "text-[#FF2E00]" : "text-white"}`}>
        {song.trackName}
      </p>
      <p className="text-[13px] text-white/55 truncate">{song.artistName}</p>
    </div>
    <span className="hidden lg:block text-xs text-white/40 font-mono tabular-nums">
      {formatTime(song.trackTimeMillis)}
    </span>
  </div>
);

/* --------------------------- Horizontal Scroll Row --------------------------- */
const ScrollRow = ({ children, testId }) => (
  <div
    data-testid={testId}
    className="flex gap-5 overflow-x-auto hide-scrollbar pb-2 -mx-4 sm:-mx-8 px-4 sm:px-8 snap-x"
  >
    {children}
  </div>
);

/* --------------------------- Section Header --------------------------- */
const SectionHeader = ({ kicker, title, subtitle }) => (
  <div className="mb-6 mt-12 first:mt-0">
    {kicker && (
      <p className="text-[11px] uppercase tracking-[0.3em] text-white/50 font-semibold mb-2">
        {kicker}
      </p>
    )}
    <div className="flex items-baseline justify-between gap-4 flex-wrap">
      <h2 className="font-display text-2xl sm:text-[32px] font-semibold tracking-tight leading-tight">
        {title}
      </h2>
      {subtitle && (
        <p className="text-sm text-white/50">{subtitle}</p>
      )}
    </div>
  </div>
);

/* --------------------------- Mood/Genre Card --------------------------- */
const MoodCard = ({ to, label, sub, gradient }) => (
  <Link
    to={to}
    className={`group relative aspect-[4/3] sm:aspect-[5/3] rounded-2xl overflow-hidden p-5 flex flex-col justify-between bg-gradient-to-br ${gradient} shadow-lg hover:scale-[1.02] transition`}
  >
    <span className="text-[11px] uppercase tracking-[0.3em] text-black/70 font-bold">{sub}</span>
    <span className="font-display text-2xl sm:text-3xl font-semibold text-black/95 leading-tight">{label}</span>
    <div className="absolute -bottom-6 -right-6 w-28 h-28 rounded-2xl rotate-12 bg-black/20 backdrop-blur" />
  </Link>
);

const MOODS = [
  { to: "/search?q=chill+vibes", label: "Chill", sub: "Mood", gradient: "from-[#A0C4FF] via-[#BDB2FF] to-[#FFC6FF]" },
  { to: "/search?q=workout+pump", label: "Workout", sub: "Activity", gradient: "from-[#FF2E00] via-[#FF8E53] to-[#FFD166]" },
  { to: "/search?q=focus+study+lo-fi", label: "Focus", sub: "Mood", gradient: "from-[#06D6A0] via-[#1FB6FF] to-[#5E60CE]" },
  { to: "/search?q=party+anthems", label: "Party", sub: "Vibe", gradient: "from-[#E2FF31] via-[#FF2E00] to-[#FF2E00]" },
  { to: "/search?q=sleep+ambient", label: "Sleep", sub: "Mood", gradient: "from-[#22223B] via-[#4A4E69] to-[#9A8C98]" },
  { to: "/search?q=throwback+classics", label: "Throwback", sub: "Era", gradient: "from-[#FFB703] via-[#FB8500] to-[#E63946]" },
];

/* --------------------------- Genre Spotlight --------------------------- */
const GenreSpotlight = ({ kicker, title, subtitle, releases, theme }) => {
  if (!releases?.length) return null;
  return (
    <>
      <SectionHeader kicker={kicker} title={title} subtitle={subtitle} />
      <div className="relative rounded-3xl overflow-hidden -mx-2 sm:mx-0">
        <div
          className="absolute inset-0 -z-10 opacity-90"
          style={{ background: theme.bg }}
        />
        <div className="p-4 sm:p-6">
          <ScrollRow testId={`row-${theme.slug}-new`}>
            {releases.map((a) => (
              <Link
                key={a.collectionId}
                to={`/album/${a.collectionId}`}
                data-testid={`${theme.slug}-release-${a.collectionId}`}
                className="group flex flex-col flex-shrink-0 w-44 sm:w-52"
              >
                <div
                  className="aspect-square rounded-2xl overflow-hidden mb-3 relative"
                  style={{ boxShadow: `0 8px 30px -8px ${theme.shadow}` }}
                >
                  <img
                    src={mediumArt(a.artworkUrl100)}
                    alt=""
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  {isAlbumNew(a.releaseDate) && <NewPill id={a.collectionId} />}
                  {a.releaseDate && (
                    <span
                      className="absolute top-2 left-2 text-[10px] uppercase tracking-wider px-2 py-1 rounded-full font-bold text-white shadow-lg"
                      style={{ background: theme.dateBg }}
                    >
                      {new Date(a.releaseDate).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                    </span>
                  )}
                  <span
                    className="absolute bottom-2 right-2 bg-black/70 backdrop-blur text-[10px] uppercase tracking-wider px-2 py-1 rounded-full font-semibold"
                    style={{ color: theme.accent }}
                  >
                    {theme.label}
                  </span>
                </div>
                <p className="text-[15px] font-medium truncate leading-tight">{a.collectionName}</p>
                <p className="text-[13px] text-white/60 truncate mt-0.5">{a.artistName}</p>
              </Link>
            ))}
          </ScrollRow>
        </div>
      </div>
    </>
  );
};

const GENRE_THEMES = {
  "hip-hop": {
    slug: "hiphop",
    label: "Hip-Hop",
    bg: "radial-gradient(800px circle at 20% 0%, rgba(255,51,102,0.18), transparent 60%), radial-gradient(700px circle at 80% 100%, rgba(226,255,49,0.10), transparent 60%), linear-gradient(135deg, #0a0a0a 0%, #1a0510 100%)",
    shadow: "rgba(255,51,102,0.4)",
    dateBg: "#FF2E00",
    accent: "#E2FF31",
  },
  "pop": {
    slug: "pop",
    label: "Pop",
    bg: "radial-gradient(800px circle at 20% 0%, rgba(255,113,206,0.20), transparent 60%), radial-gradient(700px circle at 80% 100%, rgba(157,76,221,0.15), transparent 60%), linear-gradient(135deg, #0a0612 0%, #1a0a20 100%)",
    shadow: "rgba(255,113,206,0.4)",
    dateBg: "#FF71CE",
    accent: "#FFD2F3",
  },
  "rnb": {
    slug: "rnb",
    label: "R&B",
    bg: "radial-gradient(800px circle at 20% 0%, rgba(157,76,221,0.20), transparent 60%), radial-gradient(700px circle at 80% 100%, rgba(255,180,80,0.10), transparent 60%), linear-gradient(135deg, #0a0810 0%, #14081c 100%)",
    shadow: "rgba(157,76,221,0.4)",
    dateBg: "#FF2E00",
    accent: "#FFD06A",
  },
  "country": {
    slug: "country",
    label: "Country",
    bg: "radial-gradient(800px circle at 20% 0%, rgba(255,183,3,0.18), transparent 60%), radial-gradient(700px circle at 80% 100%, rgba(230,57,70,0.10), transparent 60%), linear-gradient(135deg, #0a0a0a 0%, #1a1208 100%)",
    shadow: "rgba(255,183,3,0.35)",
    dateBg: "#FB8500",
    accent: "#FFD166",
  },
  "indie": {
    slug: "indie",
    label: "Indie",
    bg: "radial-gradient(800px circle at 20% 0%, rgba(6,214,160,0.16), transparent 60%), radial-gradient(700px circle at 80% 100%, rgba(31,182,255,0.12), transparent 60%), linear-gradient(135deg, #050a0a 0%, #081420 100%)",
    shadow: "rgba(6,214,160,0.35)",
    dateBg: "#06D6A0",
    accent: "#E2FF31",
  },
};

/* --------------------------- Home --------------------------- */
const Home = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newReleases, setNewReleases] = useState([]);
  const [genreReleases, setGenreReleases] = useState({
    "hip-hop": [],
    "pop": [],
    "rnb": [],
    "country": [],
    "indie": [],
  });
  const [risingArtists, setRisingArtists] = useState([]);
  const [top100, setTop100] = useState(null);
  const [cities, setCities] = useState([]);
  const [activeCity, setActiveCity] = useState(0);
  const { playTracks, current, playing, togglePlay } = usePlayer();
  const { user } = useAuth();

  useEffect(() => {
    let mounted = true;
    api.get("/featured")
      .then((r) => { if (mounted) setData(r.data); })
      .catch(() => {})
      .finally(() => mounted && setLoading(false));
    api.get("/featured/new-releases?limit=14")
      .then((r) => { if (mounted) setNewReleases(r.data?.results || []); })
      .catch(() => {});
    api.get("/featured/rising-artists")
      .then((r) => { if (mounted) setRisingArtists(r.data?.results || []); })
      .catch(() => {});
    api.get("/charts/top100?limit=100")
      .then((r) => { if (mounted) setTop100(r.data); })
      .catch(() => {});
    api.get("/charts/cities?limit=10")
      .then((r) => { if (mounted) setCities(r.data?.cities || []); })
      .catch(() => {});
    // Genre spotlight fetches — staggered so we don't hammer iTunes simultaneously
    const genres = ["hip-hop", "pop", "rnb", "country", "indie"];
    genres.forEach((g, i) => {
      setTimeout(() => {
        api.get(`/featured/new-releases?genre=${g}&limit=12`)
          .then((r) => {
            if (mounted) {
              setGenreReleases((prev) => ({ ...prev, [g]: r.data?.results || [] }));
            }
          })
          .catch(() => {});
      }, i * 250);
    });
    return () => { mounted = false; };
  }, []);

  // Coming Soon (AI-tracked upcoming releases) row
  const [upcomingReleases, setUpcomingReleases] = useState([]);
  useEffect(() => {
    let mounted = true;
    api.get("/upcoming/feed", { params: { limit: 8 } })
      .then((r) => { if (mounted) setUpcomingReleases(r.data?.results || []); })
      .catch(() => {});
    return () => { mounted = false; };
  }, []);

  // Recently Uploaded — public UGC feed from premium artists
  const [recentUploads, setRecentUploads] = useState([]);
  useEffect(() => {
    let mounted = true;
    api.get("/uploads/recent", { params: { limit: 12 } })
      .then((r) => { if (mounted) setRecentUploads(r.data?.results || []); })
      .catch(() => {});
    return () => { mounted = false; };
  }, []);

  // Daily-rotating home hero (mainstream / upcoming / rising — deterministic
  // per UTC date so all listeners see the same pick today).
  const [dailyHero, setDailyHero] = useState(null);
  useEffect(() => {
    let mounted = true;
    api.get("/featured/daily-hero")
      .then((r) => { if (mounted) setDailyHero(r.data); })
      .catch(() => {});
    return () => { mounted = false; };
  }, []);

  // OUT NOW — just-dropped releases from the AI/socials tracker, recent
  // platform uploads, and artist-submitted releases whose date just
  // passed. 14-day window. Each row carries socials so the card can
  // deep-link the artist's Instagram / Twitter when present.
  const [outNow, setOutNow] = useState([]);
  useEffect(() => {
    let mounted = true;
    api.get("/upcoming/out-now", { params: { limit: 12, days: 14 } })
      .then((r) => { if (mounted) setOutNow(r.data?.results || []); })
      .catch(() => {});
    return () => { mounted = false; };
  }, []);

  const greeting = useMemo(() => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 18) return "Good afternoon";
    return "Good evening";
  }, []);

  // Pick the hero album: prefer the daily rotating pick, fall back to the
  // first /featured album so the page never has a blank hero on first paint.
  const heroPick = dailyHero?.hero;

  // Top Hits row → "Top Songs" numbered list (first 12)
  const topSongs = useMemo(() => {
    const row = data?.rows?.find((r) => /top|hits/i.test(r.title)) || data?.rows?.[0];
    return (row?.results || []).filter((t) => t.previewUrl).slice(0, 12);
  }, [data]);

  // Hero play list: daily-hero's songs[] if provided, else fall back to topSongs.
  const heroPlayList = (dailyHero?.songs?.length ? dailyHero.songs : topSongs);

  // Build a fallback iTunes-album-shaped hero from /featured if daily-hero
  // failed (e.g. first time render before fetch completes).
  const fallbackHero = data?.albums?.[0] ? {
    kind: "album",
    collectionId: data.albums[0].collectionId,
    collectionName: data.albums[0].collectionName,
    artistName: data.albums[0].artistName,
    artistId: data.albums[0].artistId,
    artworkUrl100: data.albums[0].artworkUrl100,
    primaryGenreName: data.albums[0].primaryGenreName,
    link: `/album/${data.albums[0].collectionId}`,
    label: `New album · ${data.albums[0].artistName}`,
    cta_play_label: "Play",
  } : null;

  return (
    <div data-testid="home-page" className="pb-16">
      {/* Greeting */}
      <div className="flex items-baseline justify-between flex-wrap gap-2 mb-6">
        <div>
          <p className="text-[11px] uppercase tracking-[0.3em] text-white/50 font-semibold mb-1.5">
            Listen Now
          </p>
          <h1 className="font-display text-[34px] sm:text-[42px] font-semibold tracking-tighter leading-none">
            {greeting}{user ? `, ${user.name?.split(" ")[0]}` : ""}
          </h1>
        </div>
        <p className="text-sm text-white/40">
          {new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}
        </p>
      </div>

      {/* HERO featured pick — rotates daily across mainstream / upcoming / rising artists */}
      {loading && !heroPick ? (
        <HeroSkeleton />
      ) : (
        (heroPick || fallbackHero) && (
          <HeroFeature
            hero={heroPick || fallbackHero}
            songs={heroPlayList}
            onPlay={() => heroPlayList.length && playTracks(heroPlayList, 0)}
          />
        )
      )}

      {/* HOTSPOT PEEK — link to the curated discovery feed. Sits high on
          the page so listeners + artists immediately see the discovery
          surface new fans land on. */}
      <Link
        to="/hotspot"
        data-testid="home-radar-cta"
        className="group block mb-12 relative overflow-hidden border border-vermilion/30 hover:border-vermilion/60 bg-gradient-to-r from-vermilion/[0.08] via-black to-vermilion/[0.05] transition rounded-sm"
      >
        <div className="flex items-center justify-between gap-5 p-6 sm:p-7">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-[0.28em] text-vermilion font-black mb-2">
              1o1sound · live discovery
            </p>
            <h2 className="font-display text-3xl sm:text-4xl font-black uppercase tracking-tight leading-none">
              Open Hotspot
            </h2>
            <p className="text-ink-secondary text-sm sm:text-base mt-2 max-w-xl">
              Tomorrow&apos;s hits, today. Promoted artists, freshest uploads,
              and the trending catalog — updated daily.
            </p>
          </div>
          <div className="hidden sm:flex items-center gap-2 text-vermilion font-black uppercase tracking-wider text-xs whitespace-nowrap">
            Tap to open <ChevronRight size={14} className="group-hover:translate-x-1 transition-transform" />
          </div>
        </div>
      </Link>

      {/* TRENDING NEW ARTISTS — moved high so new + returning users see them */}
      {risingArtists.length > 0 && (
        <>
          <div className="mb-6">
            <p className="text-[11px] uppercase tracking-[0.3em] text-vermilion font-black mb-2">Popular Now</p>
            <div className="flex items-baseline justify-between gap-4 flex-wrap">
              <h2 className="font-display text-3xl sm:text-4xl font-black uppercase tracking-tight">Popular Artists</h2>
              <Link
                to="/submit-artist"
                data-testid="home-submit-artist-link-top"
                className="text-sm text-white/55 hover:text-vermilion transition flex items-center gap-1 font-semibold uppercase tracking-wider"
              >
                Submit your artist <ChevronRight size={14} />
              </Link>
            </div>
          </div>
          <ScrollRow testId="row-rising-artists-top">
            {risingArtists.map((a, i) => (
              <Link
                key={`top-${a.artistId}`}
                to={`/artist/${a.artistId}`}
                data-testid={`rising-artist-top-${a.artistId}`}
                className="group flex flex-col flex-shrink-0 w-40 sm:w-48 transition-transform hover:-translate-y-1 duration-300"
              >
                <ArtistAvatar
                  artist={a}
                  className="aspect-[3/4] bg-obsidian-elevated"
                >
                  <span className="absolute top-2 right-2 bg-vermilion text-white text-[10px] uppercase tracking-[0.18em] font-black px-2 py-1 rounded-sm z-10">
                    #{i + 1}
                  </span>
                  {a._community && (
                    <span className="absolute top-2 left-2 bg-[#E2FF31] text-black text-[9px] uppercase tracking-[0.18em] font-black px-2 py-1 rounded-sm z-10">
                      Community
                    </span>
                  )}
                  <div className="absolute inset-x-0 bottom-0 p-3 z-10">
                    <p className="font-display font-black text-white text-base leading-tight uppercase tracking-tight truncate">{a.displayName || a.artistName}</p>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-vermilion font-black mt-1 truncate">{a.primaryGenreName || "Artist"}</p>
                  </div>
                </ArtistAvatar>
              </Link>
            ))}
            <Link
              to="/submit-artist"
              data-testid="rising-artist-add-cta-top"
              className="group flex flex-col flex-shrink-0 w-40 sm:w-48"
            >
              <div className="relative aspect-[3/4] border-2 border-dashed border-white/15 group-hover:border-vermilion flex flex-col items-center justify-center gap-2 bg-white/[0.02] group-hover:bg-vermilion/5 transition-all">
                <div className="w-12 h-12 rounded-full bg-vermilion text-white flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Plus size={26} strokeWidth={2.5} />
                </div>
                <span className="text-[10px] uppercase tracking-[0.2em] text-white/55 group-hover:text-vermilion font-black transition-colors text-center px-3">
                  Suggest an artist you wanna know
                </span>
              </div>
            </Link>
          </ScrollRow>
        </>
      )}
      {/* COMING SOON — AI-tracked upcoming releases */}
      {/* OUT NOW — sister row to "Coming Soon". Same AI/socials data
          pipeline, just the past 14 days of drops instead of the future.
          Each card carries the artist's verified social handles when
          available so listeners can deep-link out to their socials. */}
      {outNow.length > 0 && (
        <section data-testid="home-out-now" className="mb-12">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-[11px] uppercase tracking-[0.3em] font-black text-vermilion mb-1.5 flex items-center gap-1.5">
                <Sparkles size={12} /> AI + Socials · Out Now
              </p>
              <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">
                Out Now
              </h2>
            </div>
            <Link
              to="/coming-soon"
              data-testid="home-out-now-link"
              className="text-sm text-white/60 hover:text-white flex items-center gap-1"
            >
              See all <ChevronRight size={14} />
            </Link>
          </div>

          <div className="flex gap-4 overflow-x-auto hide-scrollbar pb-2 -mx-4 px-4">
            {outNow.map((r, i) => {
              const dateStr = r.expected_release_date
                ? new Date(r.expected_release_date).toLocaleDateString(undefined, { month: "short", day: "numeric" })
                : "Today";
              const socials = r.socials || {};
              const ig = socials.instagram;
              const tw = socials.twitter || socials.x;
              const tt = socials.tiktok;
              return (
                <article
                  key={`outnow-${r.artist_id || r.track_id || i}-${r.release_title}-${i}`}
                  data-testid={`home-out-now-${i}`}
                  className="group flex-shrink-0 w-64 rounded-2xl bg-gradient-to-br from-vermilion/15 via-white/[0.04] to-white/[0.01] border border-vermilion/30 hover:border-vermilion/60 transition p-4"
                >
                  <Link
                    to={r.artist_id ? `/artist/${r.artist_id}` : "/coming-soon"}
                    className="block"
                  >
                    <div className="aspect-square mb-3 rounded-xl overflow-hidden relative">
                      <SmartImage
                        src={mediumArt(r.artwork_url || "")}
                        alt={r.release_title || ""}
                        seed={r.release_title || r.artist_name || "?"}
                        className="absolute inset-0 w-full h-full"
                        imgClassName="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        tileStyle={{ borderRadius: 0 }}
                      />
                      <span className="absolute top-2 left-2 text-[9px] font-black uppercase tracking-[0.18em] px-2 py-0.5 rounded-sm bg-vermilion text-white shadow-[0_4px_14px_-2px_rgba(232,69,37,0.7)] animate-pulse">
                        Out Now
                      </span>
                      <span className="absolute bottom-2 right-2 px-2 py-0.5 rounded-full bg-black/70 backdrop-blur text-[10px] font-semibold text-white">
                        {dateStr}
                      </span>
                    </div>
                    <p className="text-[10px] uppercase tracking-wider text-vermilion font-black mb-0.5">
                      {r.release_type || "release"}
                    </p>
                    <h3 className="font-semibold text-sm leading-tight truncate">{r.release_title}</h3>
                    <p className="text-xs text-white/55 truncate">{r.artist_name}</p>
                  </Link>
                  {/* Social-handle deep-links — only render the ones the
                      AI/socials scan actually verified for this artist. */}
                  {(ig || tw || tt) && (
                    <div className="flex items-center gap-2 mt-3 pt-3 border-t border-white/5">
                      {ig && (
                        <a href={ig} target="_blank" rel="noopener noreferrer" data-testid={`home-out-now-${i}-ig`} className="text-[10px] font-bold uppercase tracking-wider text-white/55 hover:text-vermilion transition" title="Instagram">IG</a>
                      )}
                      {tw && (
                        <a href={tw} target="_blank" rel="noopener noreferrer" data-testid={`home-out-now-${i}-x`} className="text-[10px] font-bold uppercase tracking-wider text-white/55 hover:text-vermilion transition" title="Twitter/X">X</a>
                      )}
                      {tt && (
                        <a href={tt} target="_blank" rel="noopener noreferrer" data-testid={`home-out-now-${i}-tt`} className="text-[10px] font-bold uppercase tracking-wider text-white/55 hover:text-vermilion transition" title="TikTok">TT</a>
                      )}
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        </section>
      )}

      {upcomingReleases.length > 0 && (
        <section data-testid="home-coming-soon" className="mb-12">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-[11px] uppercase tracking-[0.3em] font-semibold text-[#E2FF31] mb-1.5 flex items-center gap-1.5">
                <Radar size={12} /> AI Release Tracker
              </p>
              <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">
                Coming Soon
              </h2>
            </div>
            <Link
              to="/coming-soon"
              data-testid="home-coming-soon-link"
              className="text-sm text-white/60 hover:text-white flex items-center gap-1"
            >
              See all <ChevronRight size={14} />
            </Link>
          </div>

          <div className="flex gap-4 overflow-x-auto hide-scrollbar pb-2 -mx-4 px-4">
            {upcomingReleases.map((r, i) => {
              const conf = Math.round((r.confidence || 0) * 100);
              const dateStr = r.expected_release_date
                ? new Date(r.expected_release_date).toLocaleDateString(undefined, { month: "short", day: "numeric" })
                : "TBA";
              return (
                <Link
                  key={`${r.artist_id}-${r.release_title}-${i}`}
                  to="/coming-soon"
                  data-testid={`home-coming-soon-${i}`}
                  className="group flex-shrink-0 w-64 rounded-2xl bg-gradient-to-br from-white/[0.04] to-white/[0.01] border border-white/8 hover:border-white/20 transition p-4"
                >
                  <div className="aspect-square mb-3 rounded-xl overflow-hidden bg-gradient-to-br from-[#FF2E00]/40 via-[#FF2E00]/30 to-[#E2FF31]/20 relative">
                    <SmartImage
                      src={mediumArt(r.artwork_url || "")}
                      alt={r.release_title || ""}
                      seed={r.release_title || r.artist_name || "?"}
                      className="absolute inset-0 w-full h-full"
                      imgClassName="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      tileStyle={{ borderRadius: 0 }}
                    />
                    <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-black/70 backdrop-blur text-[9px] font-bold uppercase tracking-wider text-[#E2FF31] flex items-center gap-1">
                      <Sparkles size={9} /> {conf}%
                    </div>
                    <div className="absolute bottom-2 right-2 px-2 py-0.5 rounded-full bg-black/70 backdrop-blur text-[10px] font-semibold flex items-center gap-1 text-white">
                      <Calendar size={10} /> {dateStr}
                    </div>
                  </div>
                  <p className="text-xs uppercase tracking-wider text-white/50 font-bold mb-0.5">
                    {r.release_type || "release"}
                  </p>
                  <h3 className="font-semibold text-sm leading-tight truncate">{r.release_title}</h3>
                  <p className="text-xs text-white/50 truncate">{r.artist_name}</p>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {/* RECENTLY UPLOADED — public UGC carousel */}
      {recentUploads.length > 0 && (
        <section data-testid="home-recent-uploads" className="mb-12">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-[11px] uppercase tracking-[0.3em] font-semibold text-[#FF99B3] mb-1.5">
                Fresh from the community
              </p>
              <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">
                Recently Uploaded
              </h2>
            </div>
          </div>

          <div className="flex gap-4 overflow-x-auto hide-scrollbar pb-2 -mx-4 px-4">
            {recentUploads.map((t, i) => (
              <button
                key={t.trackId}
                onClick={() => playTracks(recentUploads, i)}
                data-testid={`home-upload-${i}`}
                className="group flex-shrink-0 w-44 rounded-xl bg-white/[0.03] border border-white/8 hover:border-white/20 transition p-3 text-left"
              >
                <div className="aspect-square mb-2 rounded-lg overflow-hidden bg-gradient-to-br from-[#FF2E00]/40 to-[#FF2E00]/30 relative">
                  {t.artworkUrl100 ? (
                    <img
                      src={t.artworkUrl100}
                      alt=""
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Play size={28} className="text-white/40" />
                    </div>
                  )}
                  <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-black/70 text-[9px] font-bold uppercase tracking-wider text-[#E2FF31]">
                    Upload
                  </span>
                </div>
                <p className="font-medium text-sm leading-tight truncate">{t.trackName}</p>
                <p className="text-xs text-white/55 truncate">{t.artistName}</p>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* TOP SONGS — Apple Music numbered grid */}
      {!loading && topSongs.length > 0 && (
        <>
          <SectionHeader kicker="Charting Now" title="Top Songs" subtitle="Updated daily" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {topSongs.map((song, i) => {
              const isCurrent = current?.trackId === song.trackId;
              const onPlay = () => {
                if (isCurrent) togglePlay();
                else playTracks(topSongs, i);
              };
              return (
                <TopSongTile
                  key={song.trackId}
                  song={song}
                  rank={i + 1}
                  onPlay={onPlay}
                  isCurrent={isCurrent}
                  isPlaying={playing}
                />
              );
            })}
          </div>
        </>
      )}

      {/* NEW MUSIC DAILY — horizontal scroll of large albums */}
      {!loading && data?.albums?.length > 0 && (
        <>
          <SectionHeader kicker="Just Added" title="New Music Daily" subtitle="Hand-picked releases" />
          {loading ? (
            <RowSkeleton />
          ) : (
            <ScrollRow testId="row-new-music">
              {data.albums.slice(0, 12).map((a) => (
                <AppleAlbumTile key={a.collectionId} album={a} large />
              ))}
            </ScrollRow>
          )}
        </>
      )}

      {/* UPCOMING ARTIST DROPS — newest releases sorted by date */}
      {newReleases.length > 0 && (
        <>
          <SectionHeader
            kicker="Fresh Drops"
            title="Upcoming Artist Drops"
            subtitle={`Updated weekly · ${newReleases.length} releases`}
          />
          <ScrollRow testId="row-upcoming-drops">
            {newReleases.map((a) => (
              <Link
                key={a.collectionId}
                to={`/album/${a.collectionId}`}
                data-testid={`upcoming-drop-${a.collectionId}`}
                className="group flex flex-col flex-shrink-0 w-44 sm:w-52"
              >
                <div className="aspect-square rounded-2xl overflow-hidden mb-3 shadow-lg relative">
                  <img
                    src={mediumArt(a.artworkUrl100)}
                    alt=""
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  {isAlbumNew(a.releaseDate) && <NewPill id={a.collectionId} />}
                  {a.releaseDate && (
                    <span className="absolute top-2 left-2 bg-black/70 backdrop-blur text-[10px] uppercase tracking-wider px-2 py-1 rounded-full font-semibold text-[#E2FF31]">
                      {new Date(a.releaseDate).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                    </span>
                  )}
                </div>
                <p className="text-[15px] font-medium truncate leading-tight">{a.collectionName}</p>
                <p className="text-[13px] text-white/55 truncate mt-0.5">{a.artistName}</p>
              </Link>
            ))}
          </ScrollRow>
        </>
      )}

      {/* TRENDING NEW ARTISTS section moved to the top of the page so new
          + returning users see emerging artists immediately. Old duplicate
          block removed during the Feb 2026 visual refresh. */}

      {/* DAILY TOP 100 — boosted with rising-artist tracks */}
      {top100?.results?.length > 0 && (
        <>
          <SectionHeader
            kicker="Updated Daily"
            title="Daily Top 100"
            subtitle={`${top100.boosted_count || 0} rising-artist boosts mixed in`}
          />
          <div className="relative rounded-3xl overflow-hidden -mx-2 sm:mx-0">
            <div
              className="absolute inset-0 -z-10"
              style={{ background: `radial-gradient(900px circle at 0% 0%, rgba(255,51,102,0.15), transparent 60%), radial-gradient(700px circle at 100% 100%, rgba(226,255,49,0.08), transparent 60%), linear-gradient(135deg, #060606 0%, #0a0a18 100%)` }}
            />
            <div className="p-4 sm:p-6 max-h-[460px] overflow-y-auto hide-scrollbar">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                {top100.results.slice(0, 20).map((song, i) => {
                  const isCurrent = current?.trackId === song.trackId;
                  const isPlayingThis = isCurrent && playing;
                  const playList = top100.results.filter((t) => t.previewUrl);
                  const onPlay = () => {
                    if (isCurrent) togglePlay();
                    else {
                      const idx = playList.findIndex((t) => t.trackId === song.trackId);
                      playTracks(playList, Math.max(idx, 0));
                    }
                  };
                  return (
                    <div
                      key={`${song.trackId}-${i}`}
                      data-testid={`top100-${song.trackId}`}
                      onClick={onPlay}
                      className="group flex items-center gap-3 p-2 pr-3 rounded-xl bg-white/[0.03] hover:bg-white/[0.08] border border-white/5 cursor-pointer transition"
                    >
                      <span className="text-2xl font-display font-semibold text-white/30 tabular-nums w-8 text-right">
                        {i + 1}
                      </span>
                      <img src={mediumArt(song.artworkUrl100)} alt="" className="w-12 h-12 rounded-lg object-cover" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <p className={`text-sm font-medium truncate ${isCurrent ? "text-[#FF2E00]" : "text-white"}`}>
                            {song.trackName}
                          </p>
                          {song._boosted && (
                            <span title="Rising Artist" className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#E2FF31]/15 text-[#E2FF31] font-bold whitespace-nowrap">
                              🔥 RISING
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-white/50 truncate">{song.artistName}</p>
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); onPlay(); }}
                        className="w-9 h-9 rounded-full bg-white/0 group-hover:bg-white text-transparent group-hover:text-black flex items-center justify-center transition-all"
                      >
                        {isPlayingThis ? <Pause size={14} fill="currentColor" /> : <Play size={14} fill="currentColor" className="ml-0.5" />}
                      </button>
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-white/40 mt-3 px-2">Scroll for more · {top100.results.length} of {top100.total || 100}</p>
            </div>
          </div>
        </>
      )}

      {cities.length > 0 && (
        <>
          <SectionHeader
            kicker="Around the World"
            title="City Charts"
            subtitle="What the world is playing right now"
          />
          {/* City tabs */}
          <div className="flex gap-2 overflow-x-auto hide-scrollbar pb-3 -mx-4 sm:-mx-8 px-4 sm:px-8">
            {cities.map((c, i) => (
              <button
                key={c.key}
                data-testid={`city-tab-${c.key}`}
                onClick={() => setActiveCity(i)}
                className={`flex-shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium border transition ${
                  activeCity === i
                    ? "bg-white text-black border-white shadow-lg"
                    : "bg-white/[0.04] text-white/70 border-white/10 hover:bg-white/[0.08]"
                }`}
              >
                <span className="text-base">{c.emoji}</span>
                <span>{c.label}</span>
              </button>
            ))}
          </div>
          {/* Active city tracks */}
          {cities[activeCity] && (
            <div data-testid={`city-content-${cities[activeCity].key}`} className="grid grid-cols-1 md:grid-cols-2 gap-2.5 fade-up">
              {cities[activeCity].results.slice(0, 10).map((song, i) => {
                const isCurrent = current?.trackId === song.trackId;
                const playList = cities[activeCity].results.filter((t) => t.previewUrl);
                const onPlay = () => {
                  if (isCurrent) togglePlay();
                  else {
                    const idx = playList.findIndex((t) => t.trackId === song.trackId);
                    playTracks(playList, Math.max(idx, 0));
                  }
                };
                return (
                  <div
                    key={`${song.trackId}-${i}`}
                    data-testid={`city-track-${song.trackId}`}
                    onClick={onPlay}
                    className="group flex items-center gap-3 p-2 pr-3 rounded-xl bg-white/[0.03] hover:bg-white/[0.08] border border-white/5 cursor-pointer transition"
                  >
                    <span className="text-xl font-display font-semibold text-white/30 tabular-nums w-7 text-right">
                      {i + 1}
                    </span>
                    <img src={mediumArt(song.artworkUrl100)} alt="" className="w-12 h-12 rounded-lg object-cover" />
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium truncate ${isCurrent ? "text-[#FF2E00]" : "text-white"}`}>
                        {song.trackName}
                      </p>
                      <p className="text-xs text-white/50 truncate">{song.artistName}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* HIP-HOP NEW MUSIC — dedicated rap/hip-hop new releases */}
      <GenreSpotlight
        kicker="Genre Spotlight"
        title="Hip-Hop New Music"
        subtitle="Freshly dropped rap & trap"
        releases={genreReleases["hip-hop"]}
        theme={GENRE_THEMES["hip-hop"]}
      />

      {/* POP NEW MUSIC */}
      <GenreSpotlight
        kicker="Genre Spotlight"
        title="Pop Right Now"
        subtitle="Catchy hooks, fresh drops"
        releases={genreReleases["pop"]}
        theme={GENRE_THEMES["pop"]}
      />

      {/* R&B / SOUL NEW MUSIC */}
      <GenreSpotlight
        kicker="Genre Spotlight"
        title="R&B & Soul"
        subtitle="Smooth & soulful new releases"
        releases={genreReleases["rnb"]}
        theme={GENRE_THEMES["rnb"]}
      />

      {/* COUNTRY NEW MUSIC */}
      <GenreSpotlight
        kicker="Genre Spotlight"
        title="Country Crossroads"
        subtitle="The latest from Nashville and beyond"
        releases={genreReleases["country"]}
        theme={GENRE_THEMES["country"]}
      />

      {/* INDIE NEW MUSIC */}
      <GenreSpotlight
        kicker="Genre Spotlight"
        title="Indie Spotlight"
        subtitle="Discover your next favorite indie act"
        releases={genreReleases["indie"]}
        theme={GENRE_THEMES["indie"]}
      />

      {/* MADE FOR YOU — mood cards */}
      <SectionHeader kicker="Made for You" title="Browse by Mood" />
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {MOODS.map((m) => (
          <MoodCard key={m.label} {...m} />
        ))}
      </div>

      {/* CURATED ROWS — each as its own horizontal scroller */}
      {!loading && data?.rows?.slice(1).map((row) => {
        const songs = (row.results || []).filter((s) => s.previewUrl);
        if (songs.length === 0) return null;
        return (
          <React.Fragment key={row.title}>
            <SectionHeader kicker="Editor's Picks" title={row.title} />
            <ScrollRow testId={`row-${row.term}`}>
              {songs.slice(0, 14).map((s) => (
                <Link
                  key={s.trackId}
                  to={s.collectionId ? `/album/${s.collectionId}` : "#"}
                  className="group flex flex-col flex-shrink-0 w-44 sm:w-52"
                >
                  <div className="aspect-square rounded-2xl overflow-hidden mb-3 shadow-lg relative">
                    <img
                      src={mediumArt(s.artworkUrl100)}
                      alt=""
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        const isC = current?.trackId === s.trackId;
                        if (isC) togglePlay();
                        else {
                          const idx = songs.findIndex((t) => t.trackId === s.trackId);
                          playTracks(songs, idx);
                        }
                      }}
                      className="absolute bottom-3 right-3 w-11 h-11 rounded-full bg-white text-black flex items-center justify-center opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all shadow-xl"
                    >
                      {current?.trackId === s.trackId && playing ? (
                        <Pause size={16} fill="currentColor" />
                      ) : (
                        <Play size={16} fill="currentColor" className="ml-0.5" />
                      )}
                    </button>
                  </div>
                  <p className="text-[15px] font-medium truncate leading-tight">{s.trackName}</p>
                  <p className="text-[13px] text-white/55 truncate mt-0.5">{s.artistName}</p>
                </Link>
              ))}
            </ScrollRow>
          </React.Fragment>
        );
      })}

      {loading && (
        <>
          <RowSkeleton />
          <RowSkeleton />
        </>
      )}
    </div>
  );
};

export default Home;
